import { useEffect, useState } from 'react';
import { BrandTemplateKind } from '@mg/shared';
import { Modal } from '../ui/Modal';
import { cn } from '../../lib/cn';
import { hcUnderlineIdle, hcUnderlineSelected } from '../../lib/forcedColors';

/**
 * How to get a usable template file OUT of a design tool.
 *
 * The export settings are not incidental: a Figma SVG exported with "Outline text" on arrives with
 * its headline as vector paths, and Generate Design then has no text to swap — the file looks
 * right and is useless. Rather than compress that into one line under the upload button, the
 * details live here, per tool, where there is room to say which checkbox matters.
 *
 * Neither format belongs to one tool, which is why each kind has TABS: an .svg comes out of
 * Illustrator as readily as Figma, and a .pptx out of PowerPoint as readily as Canva.
 */

/** One numbered step, optionally with the settings that step is really about. */
type Step = { text: string; bullets?: string[] };

type Guide = { tool: string; steps: Step[] };

const GUIDES: Record<BrandTemplateKind, Guide[]> = {
  [BrandTemplateKind.FIGMA_SVG]: [
    {
      tool: 'Figma',
      steps: [
        { text: 'Put Figma in Design mode, and select the frame you want to export.' },
        { text: 'Open the export panel on the bottom right of Figma.' },
        { text: 'Change the export type dropdown to SVG.' },
        {
          text: 'Click the 3 dots menu and make sure the following options are set:',
          bullets: [
            'Ignore overlapping layers — not checked',
            'Include Bounding Box — checked',
            'Include “id” attribute — checked',
            'Outline text — not checked',
          ],
        },
        { text: 'Once these settings are in place, export the file.' },
      ],
    },
    {
      tool: 'Adobe Illustrator',
      steps: [
        { text: 'Open the Adobe Illustrator file, and select the layers you want to export.' },
        {
          text: 'Right click, and choose “Collect for Export → Single Asset” from the contextual menu.',
        },
        {
          text: 'Go to the Asset Export panel, and select the asset you collected in the previous step.',
        },
        {
          text: 'In the Export Settings pane, choose Format = SVG, and keep the type live:',
          bullets: [
            'Type / Fonts — SVG, not “Convert to outlines”',
            'Any layer you want Smorgas Social to rewrite must still be live type — a layer that had Type → Create Outlines applied to it exports as paths, and there is no text left to swap',
          ],
        },
        {
          text: 'Click the Export button, find the folder where you wish to save, and then click the Choose button to finalize the export.',
        },
      ],
    },
  ],
  [BrandTemplateKind.CANVA_PPTX]: [
    {
      tool: 'Canva',
      steps: [
        { text: 'Open the Canva file, find the page you want to export, and click the share button.' },
        { text: 'Click the download button in the dropdown.' },
        { text: 'Click the filetype dropdown.' },
        { text: 'Change the filetype to PPTX.' },
        { text: 'Under Select Pages, choose “This Page”.' },
        { text: 'Click the button at the bottom of the dropdown to download the file.' },
      ],
    },
    {
      tool: 'PowerPoint',
      steps: [
        { text: 'Open the PowerPoint file, find the slide you want to export.' },
        { text: 'Open the File menu and click New Presentation.' },
        { text: 'Drag the slide from the existing presentation into the new presentation.' },
        { text: 'Delete any extra blank slides, and save the file.' },
      ],
    },
  ],
};

const TITLE: Record<BrandTemplateKind, string> = {
  [BrandTemplateKind.FIGMA_SVG]: 'Export an SVG template',
  [BrandTemplateKind.CANVA_PPTX]: 'Export a PPTX template',
};

export function TemplateExportGuideModal({
  open,
  kind,
  onClose,
}: {
  open: boolean;
  /** Null while closed — the modal is shared by both sections, one at a time. */
  kind: BrandTemplateKind | null;
  onClose: () => void;
}) {
  const [tool, setTool] = useState(0);

  // Back to the first tab whenever a different section opens this, so the Canva guide never opens
  // showing the tab someone last read in the SVG one.
  useEffect(() => {
    if (open) setTool(0);
  }, [open, kind]);

  if (!kind) return null;
  const guides = GUIDES[kind];
  const active = guides[Math.min(tool, guides.length - 1)];

  return (
    <Modal open={open} onClose={onClose} title={TITLE[kind]} size="lg">
      <div className="flex gap-1 border-b border-line" role="tablist" aria-label="Design tool">
        {guides.map((g, i) => (
          <button
            key={g.tool}
            type="button"
            role="tab"
            aria-selected={i === tool}
            onClick={() => setTool(i)}
            className={cn(
              'focus-ring -mb-px border-b-2 px-4 py-2 text-sm font-medium',
              i === tool
                ? 'border-action text-ink'
                : 'border-transparent text-ink-muted hover:text-ink',
              i === tool ? hcUnderlineSelected : hcUnderlineIdle,
            )}
          >
            {g.tool}
          </button>
        ))}
      </div>

      <ol className="mt-4 space-y-3 text-sm text-ink-strong">
        {active.steps.map((step, i) => (
          <li key={step.text} className="flex gap-3">
            {/* A drawn number rather than a list marker: the steps wrap, and a marker outside the
                content box leaves the second line hanging under the digit. */}
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-chip text-xs font-semibold text-ink-label"
              aria-hidden
            >
              {i + 1}
            </span>
            <div className="min-w-0">
              <p>{step.text}</p>
              {step.bullets && (
                <ul className="mt-1.5 space-y-1 text-ink-label">
                  {step.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <span className="text-ink-ghost" aria-hidden>
                        •
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Modal>
  );
}
